'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Plus, Pencil, Trash2, Copy, LayoutTemplate, Package, ArrowRight } from 'lucide-react';
import { Template } from '@/lib/types';
import { getTemplates, deleteTemplate, upsertTemplate, generateTemplateId } from '@/lib/storage';
import ConfirmModal from '@/components/ui/ConfirmModal';
import ToastContainer, { showToast } from '@/components/ui/Toast';

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<Template | null>(null);

  useEffect(() => {
    setTemplates(getTemplates());
  }, []);

  const handleDelete = (template: Template) => {
    deleteTemplate(template.id);
    setTemplates(getTemplates());
    setDeleteConfirm(null);
    showToast(`Template "${template.name}" deleted`, 'success');
  };

  const handleDuplicate = (template: Template) => {
    const newTemplate: Template = {
      ...template,
      id: generateTemplateId(),
      name: `${template.name} (Copy)`,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    upsertTemplate(newTemplate);
    setTemplates(getTemplates());
    showToast(`Template duplicated as "${newTemplate.name}"`, 'success');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
            <LayoutTemplate size={20} className="text-primary" />
            Project Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage reusable line item templates for quick quotation creation
          </p>
        </div>
        <Link
          href="/templates/new"
          className="btn-primary"
        >
          <Plus size={16} />
          New Template
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-background">
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Package size={32} className="text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">No templates yet</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md">
              Create your first template to quickly populate quotations with pre-defined line items.
            </p>
            <Link href="/templates/new" className="btn-primary">
              <Plus size={16} />
              Create Template
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {template.name}
                    </h3>
                    {template.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Default
                      </span>
                    )}
                  </div>
                  <span className="flex-shrink-0 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                    {template.items.length} item{template.items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {template.description || 'No description'}
                </p>

                {/* Item preview */}
                <div className="text-xs text-muted-foreground mb-4 space-y-1">
                  {template.items.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="truncate flex items-center gap-2">
                      <span className="w-4 h-4 rounded bg-muted flex items-center justify-center text-[10px] font-medium">
                        {idx + 1}
                      </span>
                      <span className="truncate">{item.description}</span>
                    </div>
                  ))}
                  {template.items.length > 3 && (
                    <div className="text-muted-foreground/60 pl-6">
                      +{template.items.length - 3} more items...
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-border">
                  <Link
                    href={`/templates/${template.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
                  >
                    <Pencil size={14} />
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDuplicate(template)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                    title="Duplicate template"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(template)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all"
                    title="Delete template"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
        variant="danger"
      />
      <ToastContainer />
    </div>
  );
}
